import { Fragment, useEffect, useState } from "react";
import GeneralExpensesTab from "@/components/GeneralExpensesTab";

// §4.1 — los cinco grupos del documento; "costo" se muestra dentro de Gastos (costo de ventas)
const GRUPOS = [
  { k: "activo", l: "1 · Activo", tipos: ["activo"] },
  { k: "pasivo", l: "2 · Pasivo", tipos: ["pasivo"] },
  { k: "capital", l: "3 · Capital", tipos: ["patrimonio"] },
  { k: "ingreso", l: "4 · Ingresos", tipos: ["ingreso"] },
  { k: "gasto", l: "5 · Gastos (incluye costo de ventas)", tipos: ["gasto", "costo"] },
];
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PlusIcon, BookOpenIcon, FilesIcon, ScalesIcon, ChartLineUpIcon, XIcon, TrashIcon, PencilSimpleIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function Accounting() {
  const [tab, setTab] = useState("catalogo");
  const [chart, setChart] = useState([]);
  const [journal, setJournal] = useState([]);
  const [openJournal, setOpenJournal] = useState(false);
  const [openLedger, setOpenLedger] = useState(null);
  const [openAccount, setOpenAccount] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [bs, setBs] = useState(null);
  const [is_, setIs_] = useState(null);

  const load = async () => {
    const [c, j, b, i] = await Promise.all([
      api.get("/accounting/chart"),
      api.get("/accounting/journal"),
      api.get("/accounting/balance-sheet"),
      api.get("/accounting/income-statement"),
    ]);
    setChart(c.data); setJournal(j.data); setBs(b.data); setIs_(i.data);
  };
  useEffect(() => { load(); }, []);

  const openLedgerFor = async (codigo) => {
    const { data } = await api.get(`/accounting/ledger/${codigo}`);
    setOpenLedger(data);
  };

  return (
    <Layout
      title="Contabilidad General"
      subtitle={`${chart.length} cuentas en catálogo · ${journal.length} partidas · Balance ${bs && Math.abs(bs.total_activo - (bs.total_pasivo + bs.total_patrimonio)) < 0.01 ? "cuadrado" : "descuadrado"}`}
      actions={
        tab === "catalogo" ? (
          <button
            onClick={() => { setEditAccount(null); setOpenAccount(true); }}
            data-testid="new-account-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"
          >
            <PlusIcon size={16} weight="bold" /> Nueva cuenta
          </button>
        ) : (
          <button onClick={() => setOpenJournal(true)} data-testid="new-journal-btn"
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={16} weight="bold" /> Nueva partida
          </button>
        )
      }
    >
      {/* Tabs */}
      <div className="border-b border-border mb-5 flex gap-1 overflow-x-auto">
        {[
          { k: "catalogo", l: "Catálogo de Cuentas", i: BookOpenIcon },
          { k: "partidas", l: "Diario General", i: FilesIcon },
          { k: "balance", l: "Balance General", i: ScalesIcon },
          { k: "resultados", l: "Estado de Resultados", i: ChartLineUpIcon },
          { k: "gastos", l: "Registro de Gastos", i: FilesIcon },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative flex items-center gap-2 whitespace-nowrap ${tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>
            <t.i size={14} weight="duotone" /> {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "catalogo" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Código</th><th className="p-3">Cuenta</th><th className="p-3">Tipo</th>
                <th className="p-3">Naturaleza</th><th className="p-3 pr-5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {GRUPOS.map((g) => {
                const rows = chart.filter((c) => g.tipos.includes(c.tipo));
                return (
                  <Fragment key={g.k}>
                    <tr className="border-t border-border bg-sidebar/60" data-testid={`grupo-${g.k}`}>
                      <td colSpan="5" className="p-2.5 pl-5 text-[11px] uppercase tracking-wider font-semibold text-agro-green">
                        {g.l} <span className="text-text-muted font-normal normal-case">· {rows.length} cuentas</span>
                      </td>
                    </tr>
              {rows.map((c) => (
                <tr key={c.id} className={`border-t border-border/40 hover:bg-white/[0.02] ${!c.es_movimiento ? "bg-white/[0.015]" : ""}`} data-testid={`account-${c.codigo}`}>
                  <td className={`p-3 pl-5 font-mono text-xs ${c.es_movimiento ? "text-agro-green" : "text-text-secondary font-semibold"}`} style={{ paddingLeft: `${20 + (c.nivel - 1) * 12}px` }}>{c.codigo}</td>
                  <td className={`p-3 ${c.es_movimiento ? "text-sm" : "text-sm font-medium text-text-primary uppercase tracking-wide"}`}>{c.nombre}</td>
                  <td className="p-3"><TipoBadge t={c.tipo} /></td>
                  <td className="p-3 text-xs text-text-secondary uppercase">{c.naturaleza}</td>
                  <td className="p-3 pr-5 text-right">
                    <div className="inline-flex items-center gap-2">
                      {c.es_movimiento && <button onClick={() => openLedgerFor(c.codigo)} data-testid={`ledger-${c.codigo}`} className="text-xs text-agro-yellow hover:underline">Ver mayor</button>}
                      <button
                        onClick={() => { setEditAccount(c); setOpenAccount(true); }}
                        data-testid={`edit-account-${c.codigo}`}
                        className="text-text-muted hover:text-agro-green p-1"
                        title="Editar"
                      >
                        <PencilSimpleIcon size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "gastos" && <GeneralExpensesTab />}

      {tab === "partidas" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">N°</th><th className="p-3">Fecha</th><th className="p-3">Concepto</th>
                <th className="p-3">Ref.</th><th className="p-3">Origen</th>
                <th className="p-3 text-right">Debe</th><th className="p-3 text-right pr-5">Haber</th>
              </tr>
            </thead>
            <tbody>
              {journal.length === 0 ? <tr><td colSpan="7" className="p-12 text-center text-text-muted">Sin partidas registradas</td></tr>
              : journal.map((j) => (
                <tr key={j.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`journal-${j.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">#{j.numero}</td>
                  <td className="p-3 text-xs text-text-secondary">{fmtDate(j.fecha)}</td>
                  <td className="p-3 text-sm">{j.concepto}</td>
                  <td className="p-3 font-mono text-xs text-text-muted">{j.referencia || "—"}</td>
                  <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-app border border-border text-text-secondary">{j.origen}</span></td>
                  <td className="p-3 text-right text-sm font-medium">{fmtL(j.total_debe)}</td>
                  <td className="p-3 pr-5 text-right text-sm font-medium">{fmtL(j.total_haber)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "balance" && bs && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BalanceCard title="ACTIVOS" rows={bs.activo} total={bs.total_activo} accent="green" />
          <div className="space-y-5">
            <BalanceCard title="PASIVOS" rows={bs.pasivo} total={bs.total_pasivo} accent="red" />
            <BalanceCard title="PATRIMONIO" rows={bs.patrimonio} total={bs.total_patrimonio} accent="yellow" />
            <div className={`bg-card border ${Math.abs(bs.total_activo - (bs.total_pasivo + bs.total_patrimonio)) < 0.01 ? "border-agro-green/40" : "border-red-500/40"} rounded-lg p-5`}>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Ecuación contable</div>
              <div className="font-heading text-lg mt-2">
                <span className="text-agro-green">{fmtL(bs.total_activo)}</span>
                <span className="text-text-muted mx-2">=</span>
                <span className="text-red-400">{fmtL(bs.total_pasivo)}</span>
                <span className="text-text-muted mx-2">+</span>
                <span className="text-agro-yellow">{fmtL(bs.total_patrimonio)}</span>
              </div>
              <div className="text-xs mt-2">
                {Math.abs(bs.total_activo - (bs.total_pasivo + bs.total_patrimonio)) < 0.01
                  ? <span className="text-agro-green inline-flex items-center gap-1"><CheckCircleIcon size={14} aria-hidden="true" /> Balance cuadrado</span>
                  : <span className="text-red-400">Diferencia: {fmtL(bs.total_activo - bs.total_pasivo - bs.total_patrimonio)}</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "resultados" && is_ && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <BalanceCard title="INGRESOS" rows={is_.ingresos} total={is_.total_ingresos} accent="green" />
          <div className="space-y-5">
            <BalanceCard title="COSTOS" rows={is_.costos} total={is_.total_costos} accent="yellow" />
            <BalanceCard title="GASTOS" rows={is_.gastos} total={is_.total_gastos} accent="red" />
            <div className={`bg-gradient-to-br ${is_.utilidad_neta >= 0 ? "from-agro-green/15" : "from-red-500/15"} via-card to-card border ${is_.utilidad_neta >= 0 ? "border-agro-green/40" : "border-red-500/40"} rounded-lg p-6`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted">Resultado neto</div>
              <div className={`font-heading text-4xl font-semibold mt-2 ${is_.utilidad_neta >= 0 ? "text-agro-green" : "text-red-400"}`} data-testid="utilidad-neta">
                {fmtL(is_.utilidad_neta)}
              </div>
              <div className="text-xs text-text-muted mt-2">{is_.utilidad_neta >= 0 ? "Utilidad del período" : "Pérdida del período"}</div>
            </div>
          </div>
        </div>
      )}

      {openJournal && <JournalModal chart={chart} onClose={() => setOpenJournal(false)} onSaved={() => { setOpenJournal(false); load(); }} />}
      {openLedger && <LedgerModal data={openLedger} onClose={() => setOpenLedger(null)} />}
      {openAccount && <AccountModal chart={chart} existing={editAccount} onClose={() => { setOpenAccount(false); setEditAccount(null); }} onSaved={() => { setOpenAccount(false); setEditAccount(null); load(); }} />}
    </Layout>
  );
}

function TipoBadge({ t }) {
  const map = {
    activo: "bg-agro-green/10 text-agro-green border-agro-green/30",
    pasivo: "bg-red-500/10 text-red-400 border-red-500/30",
    patrimonio: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
    ingreso: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    gasto: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    costo: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  };
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${map[t]}`}>{t}</span>;
}

function BalanceCard({ title, rows, total, accent }) {
  const accentColor = { green: "text-agro-green", red: "text-red-400", yellow: "text-agro-yellow" }[accent];
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={`bs-${title.toLowerCase()}`}>
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">{title}</h3>
        <span className={`font-heading text-lg font-semibold ${accentColor}`}>{fmtL(total)}</span>
      </div>
      <table className="w-full">
        <tbody>
          {rows.length === 0 ? <tr><td className="p-6 text-center text-text-muted text-sm">Sin saldos</td></tr>
          : rows.map((r) => (
            <tr key={r.codigo} className="border-b border-border/30">
              <td className="p-2.5 pl-5 font-mono text-xs text-text-muted">{r.codigo}</td>
              <td className="p-2.5 text-sm text-text-primary">{r.nombre}</td>
              <td className="p-2.5 pr-5 text-right text-sm font-medium">{fmtL(r.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JournalModal({ chart, onClose, onSaved }) {
  const movables = chart.filter((c) => c.es_movimiento);
  const [concepto, setConcepto] = useState("");
  const [referencia, setReferencia] = useState("");
  const [fecha, setFecha] = useState("");
  const [lineas, setLineas] = useState([
    { cuenta_codigo: "", debe: 0, haber: 0, descripcion: "" },
    { cuenta_codigo: "", debe: 0, haber: 0, descripcion: "" },
  ]);

  const updateLine = (i, patch) => {
    const next = [...lineas]; next[i] = { ...next[i], ...patch };
    setLineas(next);
  };
  const addLine = () => setLineas([...lineas, { cuenta_codigo: "", debe: 0, haber: 0, descripcion: "" }]);
  const removeLine = (i) => setLineas(lineas.filter((_, idx) => idx !== i));

  const totalD = lineas.reduce((s, l) => s + Number(l.debe || 0), 0);
  const totalH = lineas.reduce((s, l) => s + Number(l.haber || 0), 0);
  const cuadrado = Math.abs(totalD - totalH) < 0.01 && totalD > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!cuadrado) return toast.error("Partida descuadrada");
    try {
      await api.post("/accounting/journal", {
        concepto, referencia, fecha: fecha ? new Date(fecha).toISOString() : undefined,
        lineas: lineas.map((l) => ({ ...l, debe: Number(l.debe || 0), haber: Number(l.haber || 0) })),
      });
      toast.success("Partida registrada"); onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: { concepto, referencia, fecha, lineas }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">Nueva partida contable</h3>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Concepto *</label>
              <input data-testid="journal-concepto" value={concepto} onChange={(e) => setConcepto(e.target.value)} required className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Referencia</label>
              <input value={referencia} onChange={(e) => setReferencia(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>

          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2 pl-4">Cuenta</th><th className="p-2">Descripción</th>
                  <th className="p-2 text-right w-32">Debe</th><th className="p-2 text-right w-32">Haber</th><th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, i) => (
                  <tr key={i} className="border-t border-border/40">
                    <td className="p-2 pl-4">
                      <select data-testid={`line-cuenta-${i}`} value={l.cuenta_codigo} onChange={(e) => updateLine(i, { cuenta_codigo: e.target.value })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs">
                        <option value="">— cuenta —</option>
                        {movables.map((m) => <option key={m.codigo} value={m.codigo}>{m.codigo} · {m.nombre}</option>)}
                      </select>
                    </td>
                    <td className="p-2"><input value={l.descripcion} onChange={(e) => updateLine(i, { descripcion: e.target.value })} placeholder="Descripción de línea" className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" /></td>
                    <td className="p-2"><input type="number" step="0.01" value={l.debe} onChange={(e) => updateLine(i, { debe: e.target.value, haber: 0 })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                    <td className="p-2"><input type="number" step="0.01" value={l.haber} onChange={(e) => updateLine(i, { haber: e.target.value, debe: 0 })} className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs text-right" /></td>
                    <td className="p-2">{lineas.length > 2 && <button aria-label="Eliminar" type="button" onClick={() => removeLine(i)} className="text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>}</td>
                  </tr>
                ))}
                <tr className="bg-sidebar border-t border-border">
                  <td colSpan="2" className="p-2 pl-4 text-xs">
                    <button type="button" onClick={addLine} className="text-agro-green text-xs hover:underline">+ Agregar línea</button>
                  </td>
                  <td className="p-2 text-right text-sm font-medium text-agro-green">{fmtL(totalD)}</td>
                  <td className="p-2 text-right text-sm font-medium text-agro-green">{fmtL(totalH)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={`p-3 rounded-md text-sm flex items-center justify-between ${cuadrado ? "bg-agro-green/10 border border-agro-green/30 text-agro-green" : "bg-agro-yellow/10 border border-agro-yellow/30 text-agro-yellow"}`}>
            <span>{cuadrado ? <><CheckCircleIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Partida cuadrada</> : `Descuadre: ${fmtL(Math.abs(totalD - totalH))}`}</span>
            <span className="text-xs text-text-muted">Debe debe ser igual a Haber</span>
          </div>

          <button type="submit" disabled={(!cuadrado) || m.saving} data-testid="journal-submit" className="w-full bg-agro-green hover:bg-agro-green-hover disabled:opacity-50 text-black font-semibold rounded-md py-3 transition-all">
            Registrar partida
          </button>
        </form>
      </div>
    </div>
  );
}

function LedgerModal({ data, onClose }) {
  const m = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Mayor general</div>
            <h3 className="font-heading text-lg font-semibold">{data.cuenta.codigo} · {data.cuenta.nombre}</h3>
            <div className="text-xs text-text-muted">Naturaleza {data.cuenta.naturaleza} · {data.movimientos.length} movimientos</div>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <div className="p-5">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-2 pl-4">Fecha</th><th className="p-2">N° partida</th><th className="p-2">Concepto</th>
                <th className="p-2 text-right">Debe</th><th className="p-2 text-right">Haber</th><th className="p-2 text-right pr-4">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.movimientos.length === 0 ? <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin movimientos</td></tr>
              : data.movimientos.map((m, i) => (
                <tr key={i} className="border-t border-border/30">
                  <td className="p-2 pl-4 text-xs">{fmtDate(m.fecha)}</td>
                  <td className="p-2 font-mono text-xs text-agro-green">#{m.numero}</td>
                  <td className="p-2 text-sm">{m.concepto}</td>
                  <td className="p-2 text-right text-sm">{m.debe ? fmtL(m.debe) : "—"}</td>
                  <td className="p-2 text-right text-sm">{m.haber ? fmtL(m.haber) : "—"}</td>
                  <td className="p-2 pr-4 text-right text-sm font-medium text-agro-green">{fmtL(m.saldo)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-sidebar border-t border-border">
                <td colSpan="5" className="p-3 pl-4 text-right text-sm uppercase tracking-wider text-text-muted">Saldo final</td>
                <td className="p-3 pr-4 text-right font-heading text-lg text-agro-green">{fmtL(data.saldo_final)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}


function AccountModal({ chart, existing, onClose, onSaved }) {
  const [form, setForm] = useState(existing || {
    codigo: "", nombre: "", tipo: "gasto", naturaleza: "deudora",
    parent_codigo: "", es_movimiento: true, activo: true,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim() || !form.nombre.trim()) {
      return toast.error("Código y nombre son obligatorios");
    }
    try {
      if (existing) {
        await api.put(`/accounting/chart/${existing.id}`, { ...form, id: existing.id });
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/accounting/chart", form);
        toast.success("Cuenta creada");
      }
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  // Cuando el usuario cambia tipo, sugerir naturaleza
  const onTipoChange = (tipo) => {
    const naturalSide = { activo: "deudora", pasivo: "acreedora", patrimonio: "acreedora",
                          ingreso: "acreedora", gasto: "deudora", costo: "deudora" }[tipo];
    setForm({ ...form, tipo, naturaleza: naturalSide || form.naturaleza });
  };

  // Possible parent codes (header accounts)
  const headers = chart.filter((c) => !c.es_movimiento);

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="account-modal">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.1 · Catálogo de cuentas</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">{existing ? "Editar cuenta" : "Nueva cuenta"}</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Código *</label>
              <input
                data-testid="account-codigo"
                value={form.codigo} disabled={!!existing}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                placeholder="ej. 1106"
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm font-mono focus:border-agro-green disabled:opacity-60"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Nombre *</label>
              <input
                data-testid="account-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo *</label>
              <select
                data-testid="account-tipo"
                value={form.tipo}
                onChange={(e) => onTipoChange(e.target.value)}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="activo">Activo</option>
                <option value="pasivo">Pasivo</option>
                <option value="patrimonio">Patrimonio (Capital)</option>
                <option value="ingreso">Ingreso</option>
                <option value="gasto">Gasto</option>
                <option value="costo">Costo de ventas (grupo Gastos)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Naturaleza</label>
              <select
                value={form.naturaleza}
                onChange={(e) => setForm({ ...form, naturaleza: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="deudora">Deudora</option>
                <option value="acreedora">Acreedora</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta padre</label>
              <select
                value={form.parent_codigo || ""}
                onChange={(e) => setForm({ ...form, parent_codigo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Sin padre —</option>
                {headers.map((h) => <option key={h.codigo} value={h.codigo}>{h.codigo} · {h.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Recibe movimientos</label>
              <select
                value={form.es_movimiento ? "1" : "0"}
                onChange={(e) => setForm({ ...form, es_movimiento: e.target.value === "1" })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="1">Sí (cuenta operativa)</option>
                <option value="0">No (header / agrupador)</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving} type="submit" data-testid="save-account-btn" className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm">
              {existing ? "Guardar cambios" : "Crear cuenta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
